import { ProductNotice, SPLIT_REMINDER } from '../ProductNotice';
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, BatteryFull, Check, ChevronRight, Circle, RotateCcw, Share2, Signal, Wifi, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { PublicBrand } from '../PublicAppHeader';
import PublicBillDialog from '../PublicBillDialog';
import PersonAvatar from '../PersonAvatar';
import {
  calculateSampleSplit, initialAssignments, sampleItems, sampleMoney,
  samplePeople, sampleSubtotal, sampleTax, sampleTotal,
} from './sampleBill';
import '../../design/public-bill.css';
import './LandingBillDemo.css';

export default function LandingBillDemo({ onClose, publicBillUrl }: { onClose: () => void; publicBillUrl?: string }) {
  const [assignments, setAssignments] = useState(initialAssignments);
  const [selectedItem, setSelectedItem] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const selectedRow = useRef<HTMLButtonElement>(null);
  const split = calculateSampleSplit(assignments);
  const currentItem = sampleItems[selectedItem];

  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
    frame.current?.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (reviewing) body.current?.scrollTo?.({ top: 0 });
    else selectedRow.current?.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
  }, [selectedItem, reviewing]);

  function togglePerson(person: number) {
    setAssignments(previous => previous.map((people, index) => index !== selectedItem ? people :
      people.includes(person) ? people.filter(value => value !== person) : [...people, person].sort()));
    setAnnouncement('');
  }

  function reset() {
    setAssignments(initialAssignments());
    setSelectedItem(0);
    setReviewing(false);
    setAnnouncement('Original split restored.');
  }

  function changeView(review: boolean) {
    setReviewing(review);
    // The same focus target remains mounted when switching the phone's screen.
    heading.current?.focus({ preventScroll: true });
  }

  return (
    <div ref={frame} className="bill-demo" role="region" aria-label="Interactive In-N-Out sample bill">
      <div className="bill-demo-screen">
        <div className="bill-demo-status" aria-hidden="true"><span>9:41</span><i /><span><Signal size={13} /><Wifi size={13} /><BatteryFull size={18} /></span></div>
        <div className="bill-demo-nav">
          <PublicBrand href="/" />
          <div><button type="button" onClick={reset} aria-label="Reset sample bill"><RotateCcw size={17} /></button><button type="button" onClick={onClose} aria-label="Close sample bill"><X size={19} /></button></div>
        </div>

        <div ref={body} className="bill-demo-body">
          <div className="bill-demo-heading"><div><h2 ref={heading} tabIndex={-1}>In-N-Out</h2><p>Friday night · Sample bill</p></div><span aria-hidden="true">🍔</span></div>
          <div className="bill-demo-overview"><div><span>Total</span><strong>{sampleMoney(sampleTotal)}</strong></div><div><span>Paid by</span><strong className="bill-demo-payer">You</strong></div></div>

          {reviewing ? (
            <section className="bill-demo-review" aria-label="Review the sample split">
              <div className="bill-demo-section-title"><h3>Shares</h3><span>3 people</span></div>
              {split.shares.map((share, index) => (
                <div className="bill-demo-share" key={share.name}>
                  <div><PersonAvatar name={share.name} index={index} /><strong>{share.name}</strong><b>{sampleMoney(share.total)}</b></div>
                  <dl><div><dt>Items</dt><dd>{sampleMoney(share.subtotal)}</dd></div><div><dt>Tax</dt><dd>{sampleMoney(share.tax)}</dd></div></dl>
                </div>
              ))}
              <ProductNotice>{SPLIT_REMINDER}</ProductNotice>
              <div className="bill-demo-review-total"><span>Total</span><strong>{sampleMoney(split.shares.reduce((sum, share) => sum + share.total, 0))}</strong></div>
            </section>
          ) : (
            <>
              <div className="bill-demo-section-title"><h3>Split items</h3><button type="button" onClick={() => { setAssignments(sampleItems.map(() => [0, 1, 2])); setAnnouncement('Split equally. $11.16 each.'); }}>Split equally</button></div>
              <p className="bill-demo-instruction">Choose an item, then who had it.</p>
              <div className="bill-demo-items" role="group" aria-label="Receipt items">
                {sampleItems.map((item, index) => (
                  <button type="button" key={item.id} ref={selectedItem === index ? selectedRow : undefined} className="bill-demo-item" aria-pressed={selectedItem === index}
                    aria-controls="demo-people" aria-label={`${item.quantity} ${item.name}, ${sampleMoney(item.amount)}, ${assignments[index].length ? `split by ${assignments[index].map(person => samplePeople[person]).join(', ')}` : 'unassigned'}`}
                    onClick={() => { setSelectedItem(index); setAnnouncement(''); }}>
                    <span className="bill-demo-item-check" aria-hidden="true">{assignments[index].length ? <Check size={14} /> : <Circle size={15} />}</span>
                    <span className="bill-demo-item-name"><strong>{item.quantity > 1 && <small>{item.quantity}× </small>}{item.name}</strong><span>{assignments[index].length ? assignments[index].map(person => samplePeople[person]).join(', ') : 'Unassigned'}</span></span>
                    <b>{sampleMoney(item.amount)}</b><ChevronRight size={14} aria-hidden="true" />
                  </button>
                ))}
              </div>
              <div className="bill-demo-charges"><div><span>Subtotal</span><span>{sampleMoney(sampleSubtotal)}</span></div><div><span>Tax · 8%</span><span>{sampleMoney(sampleTax)}</span></div></div>
            </>
          )}
        </div>

        <div className="bill-demo-dock">
          {!reviewing && <>
            <div className="bill-demo-picker-title" id="demo-people-title">Who had {currentItem.name}?</div>
            <div id="demo-people" className="bill-demo-people" role="group" aria-labelledby="demo-people-title">
              {samplePeople.map((name, index) => <button key={name} type="button" aria-pressed={assignments[selectedItem].includes(index)} aria-label={`Include ${name} in ${currentItem.name}`} onClick={() => togglePerson(index)}>
                <span className="bill-demo-choice-avatar"><PersonAvatar name={name} index={index} />{assignments[selectedItem].includes(index) && <span className="bill-demo-choice-check"><Check size={10} /></span>}</span>
                <strong>{name}</strong><span>{sampleMoney(split.shares[index].total)}</span>
              </button>)}
            </div>
            <div className="bill-demo-live" role="status" aria-atomic="true">{announcement || (split.unassignedCount ? `${split.unassignedCount} ${split.unassignedCount === 1 ? 'item' : 'items'} left to assign · ${sampleMoney(split.unassigned)}` : `You ${sampleMoney(split.shares[0].total)} · Sam ${sampleMoney(split.shares[1].total)} · Jo ${sampleMoney(split.shares[2].total)}`)}</div>
          </>}
          {reviewing ? <div className="bill-demo-review-actions">
            {publicBillUrl && <button type="button" className="vs-button vs-button--primary vs-button--full" onClick={() => { frame.current?.scrollIntoView?.({ block: 'nearest' }); setSharing(true); }}><Share2 size={17} aria-hidden="true" /> Share bill</button>}
            <button type="button" className={`vs-button vs-button--${publicBillUrl ? 'quiet' : 'primary'} vs-button--full`} onClick={() => changeView(false)}><ArrowLeft size={16} aria-hidden="true" /> Keep splitting</button>
          </div> :
            <div className="bill-demo-actions"><button type="button" className="vs-button vs-button--secondary" onClick={() => { setSelectedItem((selectedItem + 1) % sampleItems.length); setAnnouncement(''); }}>Next item</button><button type="button" className="vs-button vs-button--primary" disabled={split.unassignedCount > 0} onClick={() => changeView(true)}>Review split</button></div>}
        </div>
        <div className="bill-demo-home" aria-hidden="true"><span /></div>
      </div>
      {publicBillUrl && <PublicBillDialog open={sharing} onClose={() => setSharing(false)} title="Share bill" container={frame.current} className="bill-demo-share-sheet">
        <div className="bill-demo-share-qr"><QRCodeSVG value={publicBillUrl} size={164} title="Scan to open the sample bill" /></div>
        <div className="bill-demo-share-copy"><p>Friends can see the breakdown<br />and pay their share.</p><span>No VibeSplit app needed.</span></div>
        <a className="vs-button vs-button--primary vs-button--full" href={publicBillUrl}>Open bill <ArrowRight size={17} aria-hidden="true" /></a>
      </PublicBillDialog>}
    </div>
  );
}
